if (!customElements.get('smileat-news-carousel-component')) {
    class SmileatNewsCarousel extends HTMLElement {
        connectedCallback() {
            this.container = this.querySelector('.smileat-news-carousel__grid');
            this.items = this.querySelectorAll('.smileat-news-carousel__item');
            this.dragInitialized = false;
            
            this.init();
            this.initWheel();
            this.initVideoPopup();

            window.addEventListener('resize', () => {
                if (this.resizeTimeout) clearTimeout(this.resizeTimeout);
                this.resizeTimeout = setTimeout(() => this.init(), 200);
            });
        }

        init() {
            this.isCarousel = window.innerWidth >= 750 ? 
                this.dataset.carouselDesktop === 'true' : 
                this.dataset.carouselMobile === 'true';

            if (this.container) {
                this.container.classList.remove('is-dragging');
                if (this.isCarousel) {
                    if (!this.dragInitialized) {
                        this.initDrag();
                        this.dragInitialized = true;
                    }
                    this.setPageOffset();
                } else {
                    this.container.style.cursor = '';
                }
            }
        }

        setPageOffset() {
            const windowWidth = window.innerWidth;
            const containerMaxWidth = 1600;
            let offset = 0;

            if (windowWidth > containerMaxWidth) {
                offset = (windowWidth - containerMaxWidth) / 2;
            }

            let basePadding = windowWidth >= 1200 ? 40 : (windowWidth >= 750 ? 24 : 15);
            const totalOffset = offset + basePadding;
            
            const wrapper = this.closest('.js-slider-container-wrapper');
            if (wrapper) wrapper.style.setProperty('--nc-page-offset', `${totalOffset}px`);
            if (this.container) this.container.style.setProperty('--nc-page-offset', `${totalOffset}px`);
        }

        initDrag() {
            const el = this.container;
            let isDown = false;
            let startX = 0;
            let scrollLeft = 0;
            this.hasDragged = false;
            let velocity = 0;
            let lastX = 0;
            let animFrame;

            const animateScroll = (target) => {
                cancelAnimationFrame(animFrame);
                const start = el.scrollLeft;
                const change = target - start;
                const duration = 600;
                let startTime = null;

                const animate = (timestamp) => {
                    if (!startTime) startTime = timestamp;
                    const progress = timestamp - startTime;
                    const t = Math.min(progress / duration, 1);
                    const val = 1 - Math.pow(1 - t, 3);
                    el.scrollLeft = start + change * val;

                    if (progress < duration) {
                        animFrame = requestAnimationFrame(animate);
                    } else {
                        el.scrollLeft = target;
                        el.style.scrollSnapType = '';
                        el.classList.remove('is-dragging');
                    }
                };
                animFrame = requestAnimationFrame(animate);
            };

            const snapToNearest = () => {
                const items = el.querySelectorAll('.smileat-news-carousel__item');
                if (items.length === 0) return;
                const step = items[0].offsetWidth + (parseInt(window.getComputedStyle(el).gap) || 0);
                const maxScroll = el.scrollWidth - el.clientWidth;
                const bias = Math.abs(velocity) > 0.5 ? (velocity > 0 ? -0.35 : 0.35) : 0;
                let targetScroll = Math.max(0, Math.min(Math.round(el.scrollLeft / step + bias) * step, maxScroll));
                animateScroll(targetScroll);
            };

            const applyMomentum = () => {
                if (Math.abs(velocity) < 0.5) {
                    snapToNearest();
                    return;
                }
                const maxScroll = el.scrollWidth - el.clientWidth;
                const next = el.scrollLeft - velocity;
                if (next <= 0 || next >= maxScroll) {
                    el.scrollLeft = Math.max(0, Math.min(next, maxScroll));
                    snapToNearest();
                    return;
                }
                el.scrollLeft = next;
                velocity *= 0.92;
                animFrame = requestAnimationFrame(applyMomentum);
            };

            const onStart = (pageX) => {
                if (window.innerWidth < 750) return;
                cancelAnimationFrame(animFrame);
                isDown = true;
                this.hasDragged = false;
                velocity = 0;
                startX = pageX - el.getBoundingClientRect().left;
                lastX = pageX;
                scrollLeft = el.scrollLeft;
                el.style.cursor = 'grabbing';
                el.style.scrollSnapType = 'none';
                el.style.scrollBehavior = 'auto';
            };

            const onEnd = () => {
                if (!isDown) return;
                isDown = false;
                el.style.cursor = 'grab';
                
                if (!this.hasDragged) {
                    el.classList.remove('is-dragging');
                    el.style.scrollSnapType = '';
                    snapToNearest();
                } else {
                    applyMomentum();
                }

                // Interaction safety timeout
                setTimeout(() => { this.hasDragged = false; }, 100);
            };

            const onMove = (pageX, e) => {
                if (!isDown || window.innerWidth < 750) return;
                const x = pageX - el.getBoundingClientRect().left;
                const walk = (x - startX) * 1.5;
                velocity = (pageX - lastX) * 1.5;
                lastX = pageX;

                if (!this.hasDragged && Math.abs(walk) > 5) {
                    this.hasDragged = true;
                    el.classList.add('is-dragging');
                }

                if (this.hasDragged) {
                    if (e && e.cancelable) e.preventDefault();
                    el.scrollLeft = scrollLeft - walk;
                }
            };

            el.addEventListener('mousedown', (e) => onStart(e.pageX));
            document.addEventListener('mouseup', onEnd);
            document.addEventListener('mousemove', (e) => onMove(e.pageX, e));

            el.addEventListener('touchstart', (e) => onStart(e.touches[0].pageX), {passive: true});
            el.addEventListener('touchend', onEnd);
            el.addEventListener('touchmove', (e) => onMove(e.touches[0].pageX, e), {passive: false});

            el.addEventListener('click', (e) => {
                if (this.hasDragged && window.innerWidth >= 750) {
                    e.preventDefault();
                    e.stopPropagation();
                }
            }, true);
        }

        initVideoPopup() {
            const sectionId = this.dataset.sectionId;
            if (!sectionId) return;

            const modal = document.getElementById(`VideoModal-${sectionId}`);
            if (!modal) return;

            const video = modal.querySelector('.smileat-video-modal__video');
            const titleEl = modal.querySelector('.smileat-video-modal__title');

            const openPopup = (card) => {
                if (this.hasDragged) return;
                
                const videoUrl = card.dataset.videoUrl;
                const cardTitle = card.querySelector('.smileat-card__title')?.innerText;

                modal.setAttribute('aria-hidden', 'false');
                document.body.style.overflow = 'hidden';
                if (titleEl) titleEl.innerText = cardTitle || '';

                if (video && videoUrl) {
                    video.src = videoUrl;
                    video.load();
                    video.play().catch(() => {});
                }
            };

            const closeModal = () => {
                modal.setAttribute('aria-hidden', 'true');
                document.body.style.overflow = '';
                if (video) {
                    video.pause();
                    video.src = '';
                }
            };

            // Event Delegation
            this.addEventListener('click', (e) => {
                const card = e.target.closest('.smileat-card--recipe');
                if (card) {
                    e.preventDefault();
                    openPopup(card);
                }
            });

            modal.querySelector('.smileat-video-modal__close')?.addEventListener('click', closeModal);
            modal.querySelector('.smileat-video-modal__overlay')?.addEventListener('click', closeModal);
            window.addEventListener('keydown', (e) => {
                if (e.key === 'Escape' && modal.getAttribute('aria-hidden') === 'false') closeModal();
            });
        }

        initWheel() {
            if (!this.container) return;
            let wheelScrolling = false;
            
            this.container.addEventListener('wheel', (e) => {
                if (window.innerWidth < 990) return;
                
                if (Math.abs(e.deltaY) > Math.abs(e.deltaX)) {
                    const isAtStart = this.container.scrollLeft <= 50;
                    const isAtEnd = this.container.scrollLeft + this.container.clientWidth >= this.container.scrollWidth - 50;

                    if ((e.deltaY > 0 && !isAtEnd) || (e.deltaY < 0 && !isAtStart)) {
                        e.preventDefault();
                        if (wheelScrolling) return; 
                        wheelScrolling = true;

                        const direction = e.deltaY > 0 ? 1 : -1;
                        const slideWidth = this.items[0]?.clientWidth || this.container.clientWidth * 0.8;
                        
                        this.container.scrollBy({
                            left: direction * slideWidth,
                            behavior: 'smooth'
                        });

                        setTimeout(() => { wheelScrolling = false; }, 600);
                    }
                }
            }, { passive: false });
        }
    }

    customElements.define('smileat-news-carousel-component', SmileatNewsCarousel);
}
